"use client";

import { Globe } from "lucide-react";
import Image from "next/image";
import { ROUTES } from "@/lib/routes";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";
import CustomButton from "./CustomButton";
import AnimateOnScroll from "./AnimateOnScroll";
import { ThemeName, CTA_BANNER_BASE_HEX, CTA_BANNER_GLOW_HEX } from "./theme";
import Link from "next/link";
import AlexLogo from "../icons/AlexLogo";
import { trackBookAppointmentClicked } from "@/lib/analytics";

const serviceCategories = [
  { label: "Counselling" },
  { label: "Allied Health" },
  { label: "Life Coach" },
];

// Service types for deep linking to booking flow
type BookingServiceType = "counselling" | "life-coach" | "occupational-therapy" | "psychology";

interface CTABannerProps {
  image?: string;
  className?: string;
  theme?: Exclude<ThemeName, "white">;
  /** Optional service type to pre-select in booking flow */
  bookingService?: BookingServiceType;
}

const CTABanner = ({
  image = "/images/v3/home/cta-tensed.png",
  className = "container-x my-10",
  theme = "blue",
  bookingService,
}: CTABannerProps) => {
  const baseHex = CTA_BANNER_BASE_HEX[theme];
  const glowHex = CTA_BANNER_GLOW_HEX[theme];

  // Generate booking URL with optional service pre-selection
  const bookingUrl = bookingService
    ? `${ROUTES.booking}?service=${bookingService}`
    : ROUTES.booking;

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORGANIZATION_NAME,
    url: SITE_URL,
    slogan: "Seize the Day, Book an Appointment Today",
    description:
      "Reach out anytime. Book online, call us, or visit our office in person.",
    makesOffer: serviceCategories.map(({ label }) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: label,
      },
    })),
    potentialAction: {
      "@type": "ReserveAction",
      name: "Book an Appointment",
      target: {
        "@type": "EntryPoint",
        urlTemplate: new URL(bookingUrl, SITE_URL).href,
        actionPlatform: [
          "https://schema.org/DesktopWebPlatform",
          "https://schema.org/MobileWebPlatform",
        ],
      },
    },
  };

  return (
    <section className={className}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema).replace(/</g, "\\u003c"),
        }}
      />

      <div
        className="relative overflow-hidden rounded-[20px] lg:min-h-[380px]"
        style={{ backgroundColor: baseHex }}
      >
        {/* Background image, spans the full card */}
        <Image
          src={image}
          alt="Book an appointment with Alexander Rodriguez"
          fill
          className="object-cover object-center"
          sizes="100vw"
        />

        {/* Gradient overlay on top of the image for text legibility */}
        <div
          className="absolute inset-0 lg:hidden"
          style={{
            background: `linear-gradient(to top, ${baseHex} 0%, ${baseHex}D9 50%, ${baseHex}4D 100%)`,
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 hidden lg:block"
          style={{
            background: `linear-gradient(to right, ${baseHex} 0%, ${baseHex}B3 50%, transparent 100%)`,
          }}
          aria-hidden
        />

        {/* Ambient glow accent */}
        <div
          className="pointer-events-none absolute -left-16 -top-16 h-72 w-72 rounded-full opacity-25 blur-[60px]"
          style={{ backgroundColor: glowHex }}
          aria-hidden
        />

        <div className="relative z-10 flex min-h-[380px] flex-col lg:items-stretch">
          <AnimateOnScroll className="flex flex-col justify-center gap-4 p-6 text-center sm:p-8 lg:max-w-xl lg:gap-3.5 lg:py-12 lg:pl-12 lg:pr-5 lg:text-left">
            <h2 className="flex items-baseline justify-center gap-3 leading-none text-white lg:justify-start">
              <Link href="/" className="shrink-0">
                <AlexLogo />
              </Link>
            </h2>

            <p
              className="font-onest leading-tight text-white/80"
              style={{ fontSize: "clamp(15px, 1.5vw, 20px)" }}
            >
              Seize the Day, Book an Appointment Today
            </p>

            <p className="mx-auto max-w-sm font-onest text-xs font-light text-white/60 lg:mx-0 lg:text-[13px]">
              Reach out anytime. Book online, call us, or visit our office in
              person.
            </p>

            <div className="mt-1.5 flex justify-center lg:justify-start">
              <CustomButton
                href={bookingUrl}
                label="Book an Appointment"
                theme={theme}
                withIcon={true}
                onClick={() =>
                  trackBookAppointmentClicked({
                    location: "cta_banner",
                    service: bookingService,
                  })
                }
              />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 font-onest text-[11px] font-semibold uppercase tracking-[0.15em] text-white lg:justify-start">
              {serviceCategories.map(({ label }) => (
                <span key={label}>{label}</span>
              ))}
            </div>

            <div className="mt-2 w-full border-t border-white/15" />

            <div className="flex flex-wrap items-center justify-center gap-5 lg:justify-start">
              <a
                href="https://www.alexrodriguez.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-onest text-sm text-white/65 transition-colors duration-200 hover:text-white"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15">
                  <Globe className="h-3.5 w-3.5" />
                </span>
                <span className="hover:underline">
                  www.alexrodriguez.com.au
                </span>
              </a>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
};

export default CTABanner;
